import React, { useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { MdBuild, MdInfoOutline, MdHelpOutline, MdList, MdAdd } from 'react-icons/md';
import PageContainer from '../../shared/layout/containers/PageContainer';
import PageHeader from '../../shared/layout/headers/PageHeader';
import PageTitle from '../../shared/layout/titles/PageTitle';
import PageContent from '../../shared/layout/containers/PageContent';
import TabBar from '../../shared/layout/tabs/TabBar';
import TabButton from '../../shared/layout/tabs/TabButton';
import StyledCard from '../../shared/layout/cards/StyledCard';
import MainButton from '../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../shared/layout/feedback/LoadingMessage';

const AuthorizedServiceCenters = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('1');
    const [loading, setLoading] = useState(false);
    const [newServiceName, setNewServiceName] = useState('');
    const [newServiceAddress, setNewServiceAddress] = useState('');
    const [newServicePhone, setNewServicePhone] = useState('');

    const authorizedServices = [
        { id: 1, name: 'Authorized Service A', address: '123 Main St, Zagreb', phone: '01 2345 678' },
        { id: 2, name: 'Authorized Service B', address: '456 Second St, Split', phone: '021 2345 678' },
        { id: 3, name: 'Authorized Service C', address: '789 Third St, Rijeka', phone: '051 2345 678' },
    ];

    const columns = [
        { name: t('AuthorizedServices.name'), selector: row => row.name, sortable: true },
        { name: t('AuthorizedServices.address'), selector: row => row.address, sortable: true },
        { name: t('AuthorizedServices.phone'), selector: row => row.phone, sortable: true },
    ];

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdBuild size={28} />}>{t('AuthorizedServices.title') || 'Service Centers'}</PageTitle>
            </PageHeader>

            <PageContent>
                <TabBar>
                    <TabButton
                        active={activeTab === '1'}
                        onClick={() => setActiveTab('1')}
                        icon={<MdList />}
                    >
                        {t('AuthorizedServices.overview')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '2'}
                        onClick={() => setActiveTab('2')}
                        icon={<MdAdd />}
                    >
                        {t('AuthorizedServices.addService')}
                    </TabButton>
                </TabBar>

                <div style={{ marginTop: '24px' }}>
                    {activeTab === '1' && (
                        <StyledCard noPadding>
                            {loading ? (
                                <LoadingMessage>{t('common.loading')}</LoadingMessage>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={authorizedServices}
                                    pagination
                                    highlightOnHover
                                    noDataComponent={<EmptyState>{t('AuthorizedServices.noServices')}</EmptyState>}
                                    customStyles={customStyles}
                                />
                            )}
                        </StyledCard>
                    )}

                    {activeTab === '2' && (
                        <StyledCard>
                            <SectionHeader>{t('AuthorizedServices.addService')}</SectionHeader>
                            <FormContainer>
                                <FormGroup>
                                    <Label>{t('AuthorizedServices.serviceName')}</Label>
                                    <Input
                                        type="text"
                                        value={newServiceName}
                                        onChange={(e) => setNewServiceName(e.target.value)}
                                        placeholder={t('AuthorizedServices.serviceName')}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('AuthorizedServices.serviceAddress')}</Label>
                                    <Input
                                        type="text"
                                        value={newServiceAddress}
                                        onChange={(e) => setNewServiceAddress(e.target.value)}
                                        placeholder={t('AuthorizedServices.serviceAddress')}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>{t('AuthorizedServices.servicePhone')}</Label>
                                    <Input
                                        type="text"
                                        value={newServicePhone}
                                        onChange={(e) => setNewServicePhone(e.target.value)}
                                        placeholder={t('AuthorizedServices.servicePhone')}
                                    />
                                </FormGroup>
                                <MainButton onClick={() => { }} style={{ alignSelf: 'flex-end' }}>
                                    {t('AuthorizedServices.create')}
                                </MainButton>
                            </FormContainer>
                        </StyledCard>
                    )}
                </div>
            </PageContent>
        </PageContainer>
    );
};

export default AuthorizedServiceCenters;

const EmptyState = styled.div`
    padding: 2.5rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.95rem;
`;

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    max-width: 600px;
`;

const SectionHeader = styled.h3`
    margin: 0 0 1.5rem 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
`;

const Label = styled.label`
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    color: #4b5563;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.75rem 0.875rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 0.95rem;
    background: #ffffff;
    transition: all 0.2s ease;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);

    &:focus {
        border-color: #821692;
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
        outline: none;
    }

    &::placeholder {
        color: #9ca3af;
    }
`;
